import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import {
    MAX_LENGTH_IN, MIN_LENGTH_IN, PRINTABLE_WIDTH_IN, SHEET_WIDTH_IN, SPACING_IN, aabb, autoBuild, autoFill,
    clampItem, contentLength, coverage, dpiLevel, dpiOf, nextUid, outOfBounds, overlapping,
} from "@/lib/gangsheet";

/** The roll the sheet is built on until the shop's own films load. */
export const DEFAULT_FILM = {
    id: "default", name: `${SHEET_WIDTH_IN}" film`, default: true,
    width_inches: SHEET_WIDTH_IN, printable_width_inches: PRINTABLE_WIDTH_IN, min_spacing_inches: SPACING_IN,
    min_length_inches: MIN_LENGTH_IN, max_length_inches: MAX_LENGTH_IN, price_per_linear_foot: 5,
};

export function useGangSheet(storeSlug) {
    const [items, setItems] = useState([]);
    const [selected, setSelected] = useState(null);
    const [lengthMode, setLengthMode] = useState("auto");
    const [sheets, setSheets] = useState(1);
    const [quote, setQuote] = useState(null);
    const [films, setFilms] = useState([DEFAULT_FILM]);
    const [filmId, setFilmId] = useState("default");
    const past = useRef([]);
    const future = useRef([]);
    const [histTick, setHistTick] = useState(0);

    const film = films.find((f) => f.id === filmId) || films[0] || DEFAULT_FILM;
    const W = film.printable_width_inches;
    const SP = film.min_spacing_inches;
    const geo = { width: W, spacing: SP };

    const commit = useCallback((next) => {
        setItems((prev) => {
            past.current = [...past.current.slice(-40), prev];
            future.current = [];
            setHistTick((t) => t + 1);
            return typeof next === "function" ? next(prev) : next;
        });
    }, []);

    // the shop's films: the default counter first, then any extra rolls it runs
    useEffect(() => {
        if (!storeSlug) return;
        api.get("/shirts/dtf/config", { params: { store: storeSlug } })
            .then(({ data }) => { setFilms(data.films?.length ? data.films : [DEFAULT_FILM]); setFilmId("default"); })
            .catch(() => setFilms([DEFAULT_FILM]));
    }, [storeSlug]);

    // a new roll is a new width: everything re-nests to fit it
    const pickFilm = (id) => {
        const next = films.find((f) => f.id === id);
        if (!next || next.id === filmId) return;
        setFilmId(id);
        commit((prev) => autoBuild(prev.map((it) => ({ ...it, wIn: Math.min(it.wIn, next.printable_width_inches), hIn: Math.round((Math.min(it.wIn, next.printable_width_inches) / it.ratio) * 100) / 100 })),
            { width: next.printable_width_inches, spacing: next.min_spacing_inches }));
    };

    const undo = () => {
        if (!past.current.length) return;
        setItems((prev) => {
            future.current = [prev, ...future.current];
            const last = past.current[past.current.length - 1];
            past.current = past.current.slice(0, -1);
            setHistTick((t) => t + 1);
            return last;
        });
    };
    const redo = () => {
        if (!future.current.length) return;
        setItems((prev) => {
            past.current = [...past.current, prev];
            const nextState = future.current[0];
            future.current = future.current.slice(1);
            setHistTick((t) => t + 1);
            return nextState;
        });
    };

    const contentLen = contentLength(items);
    const lengthIn = lengthMode === "auto" ? Math.max(film.min_length_inches, Math.ceil(contentLen * 2) / 2) : lengthMode;

    useEffect(() => {
        if (!storeSlug) return;
        api.post("/shirts/dtf/pricing", { length_in: lengthIn, quantity: sheets, store: storeSlug, film_id: filmId })
            .then(({ data }) => setQuote(data)).catch(() => { });
    }, [lengthIn, sheets, storeSlug, filmId]);

    const conflicts = useMemo(() => overlapping(items, SP), [items, SP]);
    const oob = useMemo(() => outOfBounds(items, W, lengthIn), [items, lengthIn, W]);
    const lowDpi = useMemo(() => items.filter((it) => dpiLevel(dpiOf(it)) === "bad").map((it) => it.uid), [items]);
    const cover = coverage(items, lengthIn, W);

    const groups = useMemo(() => {
        const map = new Map();
        items.forEach((it) => {
            const g = map.get(it.fileId) || { fileId: it.fileId, name: it.name, url: it.url, file: it.file, px: it.px, uids: [], wIn: it.wIn, hIn: it.hIn };
            g.uids.push(it.uid);
            map.set(it.fileId, g);
        });
        return [...map.values()];
    }, [items]);

    const fitW = (wIn) => Math.min(W, Math.max(0.5, wIn));

    const addItem = (base) => {
        const w = fitW(base.wIn);
        const seed = { ...base, wIn: w, hIn: Math.round((w / base.ratio) * 100) / 100, uid: nextUid(), rot: 0, x: 0, y: 0 };
        commit((prev) => autoBuild([...prev, seed], geo));
    };

    const updateItem = (uid, patch, { history = true } = {}) => {
        const apply = (prev) => prev.map((it) => (it.uid === uid ? clampItem({ ...it, ...patch }, W) : it));
        history ? commit(apply) : setItems(apply);
    };

    const resizeItem = (uid, wIn) => {
        setItems((prev) => prev.map((it) => {
            if (it.uid !== uid) return it;
            const w = fitW(wIn);
            return clampItem({ ...it, wIn: w, hIn: Math.round((w / it.ratio) * 100) / 100 }, W);
        }));
    };

    const removeItem = (uid) => { commit((prev) => prev.filter((it) => it.uid !== uid)); setSelected(null); };
    const removeGroup = (fileId) => { commit((prev) => prev.filter((it) => it.fileId !== fileId)); setSelected(null); };

    const duplicateItem = (uid) => commit((prev) => {
        const src = prev.find((it) => it.uid === uid);
        if (!src) return prev;
        const copy = { ...src, uid: nextUid(), x: src.x + SP, y: src.y + SP };
        return [...prev, clampItem(copy, W)];
    });

    const rotateItem = (uid, deg = 90) => commit((prev) => prev.map((it) => (it.uid === uid ? clampItem({ ...it, rot: ((it.rot || 0) + deg) % 360 }, W) : it)));

    const centerItem = (uid) => commit((prev) => prev.map((it) => {
        if (it.uid !== uid) return it;
        return clampItem({ ...it, x: (W - aabb(it).w) / 2 }, W);
    }));

    const tidy = () => commit((prev) => autoBuild(prev, geo));

    const fillFrom = (uid) => commit((prev) => {
        const seed = prev.find((it) => it.uid === uid);
        if (!seed) return prev;
        const target = lengthMode === "auto" ? undefined : lengthMode;
        return [...prev, ...autoFill(seed, prev, { ...geo, targetLength: target })];
    });

    const setGroupQty = (fileId, qty) => commit((prev) => {
        const of = prev.filter((it) => it.fileId === fileId);
        const others = prev.filter((it) => it.fileId !== fileId);
        let next = [...of];
        if (qty > of.length) {
            const seed = of[0];
            for (let i = of.length; i < qty; i++) next.push({ ...seed, uid: nextUid() });
        } else {
            next = of.slice(0, Math.max(1, qty));
        }
        return autoBuild([...others, ...next], geo);
    });

    const setGroupWidth = (fileId, wIn) => commit((prev) => autoBuild(prev.map((it) => {
        if (it.fileId !== fileId) return it;
        const w = fitW(wIn);
        return { ...it, wIn: w, hIn: Math.round((w / it.ratio) * 100) / 100 };
    }), geo));

    const clearAll = () => { commit([]); setSelected(null); };

    return {
        items, setItems, commit, selected, setSelected,
        lengthMode, setLengthMode, lengthIn, contentLen, sheets, setSheets, quote,
        films, film, filmId, pickFilm,
        conflicts, oob, lowDpi, cover, groups,
        addItem, updateItem, resizeItem, removeItem, removeGroup, duplicateItem, rotateItem,
        centerItem, tidy, fillFrom, setGroupQty, setGroupWidth, clearAll,
        undo, redo, canUndo: past.current.length > 0, canRedo: future.current.length > 0, histTick,
    };
}
